import React, { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Input } from '@/components/ui/input';
import { colors } from '@/design-system/colors';
import { useAnchoredList } from '@/hooks/useAnchoredList';

/** What the dropdown shows for one match, and what the caller gets back when it is picked (`data` is the caller's own record). */
export interface ItemSuggestion<T = unknown> {
  id: string;
  name: string;
  /** e.g. "8100 · Doors" */
  costCode: string;
  description: string;
  unit: string;
  /** the second line of the option: what tells apart items with similar names ("8100 · Doors · ea · $145.00") */
  detail: string;
  data: T;
}

interface GenericItemAutocompleteProps<T> {
  /** the text of the box (what the user typed or the picked name) */
  value: string;
  onChange: (text: string) => void;
  onPick: (suggestion: ItemSuggestion<T>) => void;
  /** matches for what was typed: asked while typing, no button */
  search: (term: string) => Promise<Array<ItemSuggestion<T>>>;
  placeholder?: string;
  disabled?: boolean;
  invalid?: boolean;
  ariaLabel?: string;
  /** extra classes for the box (a compact table passes its own field density) */
  inputClassName?: string;
}

const DEBOUNCE_MS = 250;

/**
 * Search-as-you-type for an item, like the address autocomplete: typing asks `search` (debounced), the matches open under the box, picking
 * one calls `onPick`. Typing text that matches nothing is allowed -- it simply stays as the text of the box (a custom item); nothing is
 * created anywhere. The list is portaled to <body> (hooks/useAnchoredList.ts) so no modal footer or scroll container can hide it.
 * Keyboard: ArrowUp/ArrowDown move through the matches, Enter picks the highlighted one, Escape closes.
 */
export function GenericItemAutocomplete<T>({ value, onChange, onPick, search, placeholder, disabled, invalid, ariaLabel, inputClassName }: GenericItemAutocompleteProps<T>) {
  const [suggestions, setSuggestions] = useState<Array<ItemSuggestion<T>>>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [highlighted, setHighlighted] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const requestRef = useRef(0);
  const searchRef = useRef(search);
  searchRef.current = search;
  const listboxId = useId();
  const { anchorRef, listRef, listStyle, isInsideList } = useAnchoredList(isOpen);

  // Only what the USER types searches (a picked name filling the box must not reopen the list)
  const [typed, setTyped] = useState('');
  useEffect(() => {
    const term = typed.trim();
    if (term === '') {
      requestRef.current += 1;
      setSuggestions([]);
      setIsLoading(false);
      return undefined;
    }
    const request = ++requestRef.current;
    setIsLoading(true);
    const timer = setTimeout(() => {
      searchRef.current(term)
        .then((found) => { if (request === requestRef.current) { setSuggestions(found); setFailed(false); setHighlighted(-1); } })
        .catch((error) => { if (request === requestRef.current) { console.error('Error searching items:', error); setSuggestions([]); setFailed(true); } })
        .finally(() => { if (request === requestRef.current) setIsLoading(false); });
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [typed]);

  useEffect(() => {
    const onMouseDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (containerRef.current && !containerRef.current.contains(target) && !isInsideList(target)) setIsOpen(false);
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  });

  const pick = (suggestion: ItemSuggestion<T>) => {
    setIsOpen(false);
    setSuggestions([]);
    setTyped('');
    onPick(suggestion);
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      setIsOpen(false);
    } else if (event.key === 'ArrowDown' && suggestions.length > 0) {
      event.preventDefault();
      setIsOpen(true);
      setHighlighted((index) => Math.min(index + 1, suggestions.length - 1));
    } else if (event.key === 'ArrowUp' && suggestions.length > 0) {
      event.preventDefault();
      setHighlighted((index) => Math.max(index - 1, 0));
    } else if (event.key === 'Enter') {
      event.preventDefault(); // Enter never submits the form this box sits in
      if (isOpen && highlighted >= 0 && suggestions[highlighted]) pick(suggestions[highlighted]);
    }
  };

  const term = typed.trim();
  const showList = isOpen && term !== '' && listStyle;

  return (
    <div ref={containerRef} className="relative">
      <div ref={anchorRef}>
        <Input
          role="combobox"
          aria-label={ariaLabel}
          aria-expanded={!!showList}
          aria-controls={listboxId}
          aria-autocomplete="list"
          aria-invalid={invalid || undefined}
          autoComplete="off"
          disabled={disabled}
          value={value}
          placeholder={placeholder}
          className={`font-form-input generic-form-field aria-[invalid=true]:border-destructive ${inputClassName ?? ''}`}
          onChange={(event) => { onChange(event.target.value); setTyped(event.target.value); setIsOpen(true); }}
          onFocus={() => { if (typed.trim() !== '') setIsOpen(true); }}
          onKeyDown={onKeyDown}
        />
      </div>
      {showList && createPortal(
        <div ref={listRef} className="fixed z-[var(--z-overlay)] bg-white border-2 border-border rounded-lg shadow-lg max-h-60 overflow-auto" style={listStyle || undefined}>
          {isLoading && suggestions.length === 0 ? (
            <div className="p-2 text-sm text-gray-500">Searching...</div>
          ) : failed ? (
            <div className="p-2 text-sm text-destructive" role="alert">Could not search the catalog</div>
          ) : (
            <ul id={listboxId} className="py-1" role="listbox">
              {suggestions.map((suggestion, index) => (
                <li
                  key={suggestion.id}
                  role="option"
                  aria-selected={index === highlighted}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => pick(suggestion)}
                  onMouseEnter={() => setHighlighted(index)}
                  className={`px-3 py-2 hover:bg-gray-100 cursor-pointer ${index === highlighted ? 'bg-gray-100' : ''}`}
                >
                  <div className="text-sm font-medium text-gray-900">{suggestion.name}</div>
                  {suggestion.detail && <div className="text-xs text-gray-500">{suggestion.detail}</div>}
                </li>
              ))}
              {suggestions.length === 0 && (
                <div className="p-2 text-sm" style={{ color: colors.dropdownMutedText }}>No matching items. Keep typing to use it as a custom item.</div>
              )}
            </ul>
          )}
        </div>,
        document.body
      )}
    </div>
  );
}
